import React, { useState, useEffect } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton } from "styles";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCardIcon from "@mui/icons-material/AddCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { WhiteButton } from "styles";
import { RedButton } from "styles";
import MDBadge from "components/MDBadge";

const USER_DATA_KEY = "userMeData";

const ProfileSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [newClientName, setNewClientName] = useState("");
  const [cardDialog, setCardDialog] = useState(null);
  const [confirmRemoveCard, setConfirmRemoveCard] = useState(null);
  const [cardForm, setCardForm] = useState({
    cardHolderName: "",
    cardNumber: "",
    expireMonth: "",
    expireYear: "",
    cvc: "",
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [billingDialogSite, setBillingDialogSite] = useState(null);
  const [billingForm, setBillingForm] = useState({
    name: "",
    address: "",
    city: "",
    district: "",
    postalCode: "",
    country: "",
    taxOffice: "",
    state: "",
    taxNumber: "",
    eInvoiceEnabled: false,
  });
  const [billingValidate, setBillingValidate] = useState(false);

  // Tarih formatlama
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD.MM.YYYY HH:mm");
  };

  // Kalan gün hesaplama
  const getDaysRemaining = (endDate) => {
    const end = dayjs(endDate);
    const now = dayjs();
    const days = end.diff(now, "day");
    return days > 0 ? `${days} gün` : "Süre doldu";
  };

  // Session storage'dan kullanıcı verisini al
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  // Kullanıcı verisini yeniden yükle
  const reloadUserData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.user) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        setUserData(data);
      } else {
        throw new Error("Kullanıcı verisi alınamadı");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Önce cache'den kontrol et
    const cachedUser = getCachedUserData();
    if (cachedUser) {
      setUserData(cachedUser);
      setLoading(false);
      return;
    }

    reloadUserData();
  }, []);

  // Müşteri adını güncelleme
  const handleUpdateClient = async () => {
    if (!editClient || !newClientName) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          clientId: editClient.clientId,
          clientName: newClientName,
        }),
      });

      if (response.ok) {
        setSuccessMsg("Müşteri adı başarıyla güncellendi");
        reloadUserData();
        setEditClient(null);
      } else {
        const data = await response.json();
        throw new Error(data.message || "Güncelleme başarısız");
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Kart ekleme/güncelleme
  const handleSaveCard = async () => {
    if (!cardDialog || !cardForm.cardHolderName || !cardForm.cardNumber) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/payment/save-card-auth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            subscriptionId: cardDialog.subscriptionId,
            card: cardForm,
          }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccessMsg(data.message || "Kart bilgileri başarıyla kaydedildi");
        reloadUserData();
        setCardDialog(null);
      } else {
        throw new Error(data.message || "Kart kaydedilemedi");
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Kartı kaldır
  const handleRemoveCard = async () => {
    if (!confirmRemoveCard) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/payment/delete-card`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            subscriptionId: confirmRemoveCard.subscriptionId,
          }),
        }
      );

      if (response.ok) {
        setSuccessMsg("Kart başarıyla kaldırıldı");
        reloadUserData();
        setConfirmRemoveCard(null);
      } else {
        const data = await response.json();
        throw new Error(data.message || "Kart kaldırılamadı");
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fatura Bilgileri Düzenleme Diyaloğu açıcı
  const openBillingDialog = (site) => {
    const existing = site?.billingInfo;
    setBillingForm(
      existing
        ? {
            ...existing,
            country: existing.country || "Türkiye",
          }
        : {
            name: "",
            address: "",
            city: "",
            district: "",
            postalCode: "",
            country: "Türkiye",
            taxOffice: "",
            state: "",
            taxNumber: "",
            eInvoiceEnabled: false,
          }
    );
    setBillingDialogSite(site);
    setBillingValidate(false);
  };

  // Billing validation helpers
  const requiredBillingFields = [
    "name",
    "address",
    "district",
    "city",
    "postalCode",
    "country",
    "taxNumber",
  ];
  const isEmpty = (v) => String(v ?? "").trim() === "";
  const hasMissingRequired = (form) =>
    requiredBillingFields.some((k) => isEmpty(form[k]));
  const isFieldError = (key) => billingValidate && isEmpty(billingForm[key]);
  const isFieldSuccess = (key) => billingValidate && !isEmpty(billingForm[key]);

  // Mobilde yazıların taşmasına izin vererek yatay kaydırmayı etkinleştir
  const mobileNowrap = { whiteSpace: { xs: "nowrap", md: "normal" } };

  // Fatura Bilgileri Kaydet
  const handleSaveBilling = async () => {
    if (!billingDialogSite) return;
    // turn on validation
    setBillingValidate(true);
    if (hasMissingRequired(billingForm)) {
      setErrorMsg("Lütfen zorunlu alanları doldurun.");
      return;
    }
    try {
      setLoading(true);
      const isCreate = !billingDialogSite?.billingInfo;

      console.log("Sending request:", {
        method: isCreate ? "POST" : "PUT",
        body: {
          siteId: billingDialogSite.siteId,
          billingInfo: billingForm,
        },
      });
      
      const response = await fetch(`${API_BASE_URL}/billing`, {
        method: isCreate ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          siteId: billingDialogSite.siteId,
          billingInfo: billingForm,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Fatura bilgileri güncellenemedi");
      }
      setSuccessMsg(
        data.message ||
          (isCreate ? "Fatura bilgisi eklendi" : "Fatura bilgileri güncellendi")
      );
      setBillingDialogSite(null);
      await reloadUserData();
    } catch (err) {
      setErrorMsg(err.message || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{error}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          {/* Müşteri ve Site Bilgileri */}
          {userData?.user?.clients?.map((client) => (
            <React.Fragment key={`client-${client.clientId}`}>
              {client.sites?.length > 0 ? (
                client.sites.map((site) => (
                  <Grid item xs={12} key={`c${client.clientId}-s${site.siteId}`}>
                    <Card sx={{ boxShadow: 3 }}>
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={3}
                        >
                          <Box>
                            <Typography variant="h5" fontWeight="bold">
                              {site.domain || "-"}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <MDBadge
                              variant="contained"
                              badgeContent={client.isActive ? "Aktif Müşteri" : "Pasif Müşteri"}
                              color={client.isActive ? "success" : "error"}
                              container
                            />
                          </Box>
                        </Box>

                        <TableContainer component={Paper} sx={{ overflowX: { xs: "auto", md: "visible" } }}>
                          <Box
                            sx={{
                              display: { xs: "block", md: "none" },
                              bgcolor: "grey.100",
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              minWidth: 720,
                              px: 2,
                              py: 1,
                            }}
                          >
                            <Grid container spacing={0}>
                              <Grid item xs={3}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                                  Şirket / Şahıs Ünvanı
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                                  Fatura Adresi
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                                  Vergi No / E‑Fatura
                                </Typography>
                              </Grid>
                              <Grid item xs={2}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                                  Fatura İşlemleri
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                          <Table
                            sx={{
                              tableLayout: { xs: "fixed", md: "fixed" },
                              width: "100%",
                              minWidth: { xs: 720, md: "unset" },
                              "& td, & th": { px: 2 },
                            }}
                          >
                            <TableHead sx={{ bgcolor: "grey.100", display: { xs: "none", md: "table-header-group" } }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", width: { xs: "25%", md: "25%" } }}>Şirket / Şahıs Ünvanı</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: { xs: "30%", md: "30%" } }}>Fatura Adresi</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: { xs: "25%", md: "25%" } }}>Vergi No / E‑Fatura</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: { xs: "20%", md: "20%" } }}>Fatura İşlemleri</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow sx={{ verticalAlign: "top" }} hover>
                                <TableCell sx={{ width: { xs: "25%", md: "25%" }, display: "table-cell" }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: { xs: "none", md: "none" },
                                      fontWeight: { xs: 700, md: 600 },
                                      color: "text.secondary",
                                      mb: 0.5,
                                      fontSize: { xs: "0.95rem" }
                                    }}
                                  >
                                    Şirket / Şahıs Ünvanı
                                  </Typography>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, ...mobileNowrap }}>
                                      {site.billingInfo?.name ? site.billingInfo.name : "—"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ width: { xs: "30%", md: "30%" } }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: { xs: "none", md: "none" },
                                      fontWeight: { xs: 700, md: 600 },
                                      color: "text.secondary",
                                      mb: 0.5,
                                      fontSize: { xs: "0.95rem" }
                                    }}
                                  >
                                    Fatura Adresi
                                  </Typography>
                                  {site.billingInfo ? (
                                    <Box sx={mobileNowrap}>
                                      <Typography variant="body2">
                                        {site.billingInfo.address
                                          ? `${site.billingInfo.address}${site.billingInfo.postalCode ? " " + site.billingInfo.postalCode : ""}`
                                          : site.billingInfo.postalCode || "—"}
                                      </Typography>
                                      <Typography variant="body2">
                                        {[site.billingInfo.district, site.billingInfo.city, site.billingInfo.state].filter(Boolean).join(" / ") || "—"}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">Fatura bilgisi yok</Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ width: { xs: "25%", md: "25%" } }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: { xs: "none", md: "none" },
                                      fontWeight: { xs: 700, md: 600 },
                                      color: "text.secondary",
                                      mb: 0.5,
                                      fontSize: { xs: "0.95rem" }
                                    }}
                                  >
                                    Vergi No / E‑Fatura
                                  </Typography>
                                  {site.billingInfo ? (
                                    <Box sx={mobileNowrap}>
                                      {site.billingInfo.taxOffice || site.billingInfo.taxNumber || site.billingInfo.vatNumber ? (
                                        <>
                                          {site.billingInfo.taxOffice && (
                                            <Typography variant="body2">VD: {site.billingInfo.taxOffice}</Typography>
                                          )}
                                          {site.billingInfo.taxNumber ? (
                                            <Typography variant="body2">VKN: {site.billingInfo.taxNumber}</Typography>
                                          ) : site.billingInfo.vatNumber ? (
                                            <Typography variant="body2">VAT: {site.billingInfo.vatNumber}</Typography>
                                          ) : null}
                                        </>
                                      ) : (
                                        <Typography variant="body2">—</Typography>
                                      )}
                                      <Box sx={{ mt: 1 }}>
                                        <MDBadge
                                          variant="contained"
                                          badgeContent={site.billingInfo.eInvoiceEnabled ? "E‑Fatura Aktif" : "E‑Fatura Kapalı"}
                                          color={site.billingInfo.eInvoiceEnabled ? "success" : "secondary"}
                                          container
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ width: { xs: "20%", md: "20%" } }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: { xs: "none", md: "none" },
                                      fontWeight: { xs: 700, md: 600 },
                                      color: "text.secondary",
                                      mb: 0.5,
                                      fontSize: { xs: "0.95rem" }
                                    }}
                                  >
                                    Fatura İşlemleri
                                  </Typography>
                                  {site.billingInfo ? (
                                    <BlueButton startIcon={<ReceiptLongIcon />} onClick={() => openBillingDialog(site)} sx={{ width: { xs: "100%", md: "auto" } }}>
                                      Bilgileri Değiştir
                                    </BlueButton>
                                  ) : (
                                    <BlueButton startIcon={<ReceiptLongIcon />} onClick={() => openBillingDialog(site)} sx={{ width: { xs: "100%", md: "auto" } }}>
                                      Fatura Bilgisi Ekle
                                    </BlueButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12} key={`c${client.clientId}-empty`}>
                  <Card sx={{ boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="body1" sx={{ mt: 2, textAlign: "center", py: 4 }}>
                        Bu müşteriye ait site bulunamadı
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      </MDBox>

      {/* Fatura Bilgileri Düzenleme Diyaloğu */}
      <Dialog
        open={!!billingDialogSite}
        onClose={() => setBillingDialogSite(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Fatura Bilgilerini Düzenle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ticari Ünvan / Ad Soyad"
                value={billingForm.name}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, name: e.target.value })
                }
                error={isFieldError("name")}
                helperText={isFieldError("name") ? "Bu alan zorunludur." : " "}
                sx={
                  isFieldSuccess("name")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adres"
                value={billingForm.address}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, address: e.target.value })
                }
                error={isFieldError("address")}
                helperText={
                  isFieldError("address") ? "Bu alan zorunludur." : " "
                }
                sx={
                  isFieldSuccess("address")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="İlçe"
                value={billingForm.district}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, district: e.target.value })
                }
                error={isFieldError("district")}
                helperText={
                  isFieldError("district") ? "Bu alan zorunludur." : " "
                }
                sx={
                  isFieldSuccess("district")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="İl"
                value={billingForm.city}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, city: e.target.value })
                }
                error={isFieldError("city")}
                helperText={isFieldError("city") ? "Bu alan zorunludur." : " "}
                sx={
                  isFieldSuccess("city")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Posta Kodu"
                value={billingForm.postalCode}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, postalCode: e.target.value })
                }
                error={isFieldError("postalCode")}
                helperText={
                  isFieldError("postalCode") ? "Bu alan zorunludur." : " "
                }
                sx={
                  isFieldSuccess("postalCode")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ülke"
                value={billingForm.country}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, country: e.target.value })
                }
                error={isFieldError("country")}
                helperText={
                  isFieldError("country") ? "Bu alan zorunludur." : " "
                }
                sx={
                  isFieldSuccess("country")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vergi Dairesi"
                value={billingForm.taxOffice}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, taxOffice: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vergi No"
                value={billingForm.taxNumber}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, taxNumber: e.target.value })
                }
                error={isFieldError("taxNumber")}
                helperText={
                  isFieldError("taxNumber") ? "Bu alan zorunludur." : " "
                }
                sx={
                  isFieldSuccess("taxNumber")
                    ? {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          { borderColor: "success.main" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "success.main",
                        },
                      }
                    : {}
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={billingForm.eInvoiceEnabled}
                    onChange={(e) =>
                      setBillingForm({
                        ...billingForm,
                        eInvoiceEnabled: e.target.checked,
                      })
                    }
                  />
                }
                label="E‑Fatura Aktif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setBillingDialogSite(null)}>
            İptal
          </WhiteButton>
          <BlueButton onClick={handleSaveBilling}>Kaydet</BlueButton>
        </DialogActions>
      </Dialog>

      {/* Müşteri Adı Düzenleme Diyaloğu */}
      <Dialog open={!!editClient} onClose={() => setEditClient(null)}>
        <DialogTitle>Müşteri Adını Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Müşteri Adı"
            type="text"
            fullWidth
            variant="outlined"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            sx={{ mt: 2, minWidth: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setEditClient(null)}>İptal</WhiteButton>
          <BlueButton
            onClick={handleUpdateClient}
            disabled={!newClientName || newClientName === editClient?.name}
          >
            Kaydet
          </BlueButton>
        </DialogActions>
      </Dialog>

      {/* Kart Ekleme/Düzenleme Diyaloğu */}
      <Dialog open={!!cardDialog} onClose={() => setCardDialog(null)}>
        <DialogTitle>
          {cardDialog?.storedCard
            ? "Kredi Kartını Güncelle"
            : "Yeni Kredi Kartı Ekle"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1, minWidth: 500 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kart Sahibi"
                value={cardForm.cardHolderName}
                onChange={(e) =>
                  setCardForm({ ...cardForm, cardHolderName: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kart Numarası"
                value={cardForm.cardNumber}
                onChange={(e) =>
                  setCardForm({ ...cardForm, cardNumber: e.target.value })
                }
                required
                inputProps={{ maxLength: 16 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Son Kullanma Ayı"
                value={cardForm.expireMonth}
                onChange={(e) =>
                  setCardForm({ ...cardForm, expireMonth: e.target.value })
                }
                required
                inputProps={{ maxLength: 2 }}
                placeholder="MM"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Son Kullanma Yılı"
                value={cardForm.expireYear}
                onChange={(e) =>
                  setCardForm({ ...cardForm, expireYear: e.target.value })
                }
                required
                inputProps={{ maxLength: 4 }}
                placeholder="YYYY"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CVC"
                value={cardForm.cvc}
                onChange={(e) =>
                  setCardForm({ ...cardForm, cvc: e.target.value })
                }
                required
                inputProps={{ maxLength: 3 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setCardDialog(null)}>İptal</WhiteButton>
          <BlueButton onClick={handleSaveCard}>
            {cardDialog?.storedCard ? "Güncelle" : "Kaydet"}
          </BlueButton>
        </DialogActions>
      </Dialog>

      {/* Kart Silme Onay Diyaloğu */}
      <Dialog
        open={!!confirmRemoveCard}
        onClose={() => setConfirmRemoveCard(null)}
      >
        <DialogTitle>Kartı Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Kartı silmek istediğinizden emin misiniz?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kayıtlı kart olmaması durumunda otomatik olarak üyeliğiniz
            güncellenmez ve bitiş tarihinde hesabınız otomatik olarak kapanır.
          </Typography>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setConfirmRemoveCard(null)}>
            İptal
          </WhiteButton>
          <RedButton onClick={handleRemoveCard}>Sil</RedButton>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Bildirimler */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={3500}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={3500}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ProfileSettings;
